const VIS_TOOLS_SLIDER_SELECTOR = "[data-vis-tools-slider]";
const VIS_TOOLS_CLONE_ATTR = "data-vis-tools-clone";
const DEFAULT_GROUP_COUNT = 3;
const DEFAULT_CARDS_PER_GROUP = 5;
const DESKTOP_DURATION_MS = 36000;
const MOBILE_DURATION_MS = 28000;
const ACTIVE_INTERVAL_MS = 250;
const SNAP_DURATION_MS = 300;
const sliderInstances = new WeakMap();

function parsePositiveInt(value, fallback) {
  const parsed = Number.parseInt(String(value || ""), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function collapseWhitespace(value) {
  const source = String(value || "");
  let result = "";
  let pendingSpace = false;
  for (const char of source) {
    if (char.charCodeAt(0) <= 32) {
      pendingSpace = true;
    } else {
      if (pendingSpace && result) result += " ";
      result += char;
      pendingSpace = false;
    }
  }
  return result.trim();
}

function buildTwoLineLabel(lines) {
  const clean = lines.map((line) => collapseWhitespace(line)).filter(Boolean);
  if (!clean.length) return "";
  if (clean.length >= 2) return `${clean[0]}\n${clean.slice(1).join(" ")}`;
  const words = clean[0].split(" ").filter(Boolean);
  if (words.length <= 1) return clean[0];
  return `${words[0]}\n${words.slice(1).join(" ")}`;
}

function collectLangLabelLines(root, selector) {
  if (!(root instanceof Element)) return [];
  const childLines = [...root.children].filter((node) => node instanceof Element && node.tagName === "SPAN");
  const sourceNodes = childLines.length ? childLines : [root];
  return sourceNodes
    .map((node) => {
      if (!(node instanceof Element)) return collapseWhitespace(node.textContent || "");
      if (selector) {
        const selected = [...node.querySelectorAll(selector)].map((item) => collapseWhitespace(item.textContent || "")).filter(Boolean);
        if (selected.length) return selected.join(" ");
      }
      return collapseWhitespace(node.textContent || "");
    })
    .filter(Boolean);
}

function hydrateLabelData(cards) {
  cards.forEach((card) => {
    const labelEl = card.querySelector(".vis-tools-logo-title");
    if (!(labelEl instanceof Element)) return;
    const deLines = collectLangLabelLines(labelEl, ".lang-de");
    const enLines = collectLangLabelLines(labelEl, ".lang-en");
    const fallbackLines = collectLangLabelLines(labelEl, "");
    const deLabel = buildTwoLineLabel(deLines.length ? deLines : fallbackLines);
    const enLabel = buildTwoLineLabel(enLines.length ? enLines : deLines.length ? deLines : fallbackLines);
    if (deLabel) {
      card.dataset.visLabelDe = deLabel;
      card.classList.add("vis-tools-label-de");
    } else {
      delete card.dataset.visLabelDe;
      card.classList.remove("vis-tools-label-de");
    }
    if (enLabel) {
      card.dataset.visLabelEn = enLabel;
      card.classList.add("vis-tools-label-en");
    } else {
      delete card.dataset.visLabelEn;
      card.classList.remove("vis-tools-label-en");
    }
  });
}

function uniqueGroupsFromCards(cards) {
  const groups = [];
  cards.forEach((card) => {
    const group = card.dataset.visGroup || "";
    if (group && !groups.includes(group)) groups.push(group);
  });
  return groups;
}

function normalizeOffset(value, shift) {
  if (!(shift > 0)) return 0;
  return ((value % shift) + shift) % shift;
}

function getDurationMs() {
  const width = window.innerWidth || document.documentElement.clientWidth || 0;
  return width < 992 ? MOBILE_DURATION_MS : DESKTOP_DURATION_MS;
}

function getReducedMotion() {
  return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;
}

function chooseClosestTarget(currentOffset, targetOffset, shift) {
  if (!(shift > 0)) return targetOffset;
  const candidates = [targetOffset, targetOffset + shift, targetOffset - shift];
  return candidates.reduce((best, candidate) => (
    Math.abs(candidate - currentOffset) < Math.abs(best - currentOffset) ? candidate : best
  ), candidates[0]);
}

function setActiveGroup(buttons, group) {
  buttons.forEach((button) => {
    const isActive = button.dataset.visGroupTarget === group;
    button.classList.toggle("is-active", isActive);
    button.setAttribute("aria-pressed", isActive ? "true" : "false");
  });
}

function applyLoopVars(slider, offset, shift, durationMs) {
  const safeOffset = Math.max(0, offset);
  const safeShift = Math.max(0, shift);
  slider.style.setProperty("--vis-loop-offset", `${safeOffset.toFixed(3)}px`);
  slider.style.setProperty("--vis-loop-shift", `${safeShift.toFixed(3)}px`);
  slider.style.setProperty("--vis-loop-from", `${(-safeOffset).toFixed(3)}px`);
  slider.style.setProperty("--vis-loop-to", `${(-safeOffset - safeShift).toFixed(3)}px`);
  slider.style.setProperty("--vis-loop-duration", `${Math.max(1, durationMs)}ms`);
  slider.style.setProperty("--vis-loop-duration-mobile", `${Math.max(1, durationMs)}ms`);
}

function initOneVisToolsSlider(scope) {
  const previous = sliderInstances.get(scope);
  if (previous) previous.cleanup();

  const slider = scope.querySelector(".vis-statement-slider");
  const track = scope.querySelector(".vis-statement-track");
  if (!(slider instanceof HTMLElement) || !(track instanceof HTMLElement)) return null;

  track.querySelectorAll(`[${VIS_TOOLS_CLONE_ATTR}="1"]`).forEach((clone) => clone.remove());

  const cardsPerGroup = parsePositiveInt(scope.dataset.visToolsCardsPerGroup, DEFAULT_CARDS_PER_GROUP);
  const groupCount = parsePositiveInt(scope.dataset.visToolsGroupCount, DEFAULT_GROUP_COUNT);
  const originalCards = [...track.children].filter((child) => (
    child instanceof HTMLElement &&
    child.classList.contains("vis-tools-logo-card") &&
    child.getAttribute(VIS_TOOLS_CLONE_ATTR) !== "1"
  ));
  const buttons = [...scope.closest("#section-vis-tools")?.querySelectorAll(".vis-tools-chip[data-vis-group-target]") || []];
  const groupOrder = buttons.map((button) => button.dataset.visGroupTarget || "").filter(Boolean);
  const fallbackGroups = uniqueGroupsFromCards(originalCards);
  const groups = groupOrder.length ? groupOrder : fallbackGroups;
  const expectedCardCount = groupCount * cardsPerGroup;

  if (!originalCards.length || !groups.length || originalCards.length !== expectedCardCount) {
    scope.classList.add("is-vis-tools-ready");
    return null;
  }

  hydrateLabelData(originalCards);

  const clones = originalCards.map((card) => {
    const clone = card.cloneNode(true);
    clone.setAttribute("aria-hidden", "true");
    clone.setAttribute(VIS_TOOLS_CLONE_ATTR, "1");
    track.appendChild(clone);
    return clone;
  });

  const pauseReasons = new Set();
  const state = {
    activeAnchor: 0,
    baseOffset: 0,
    durationMs: getDurationMs(),
    groupWidth: 0,
    loopShift: 0,
    measured: false,
    pauseOffset: 0,
    paused: false,
    resizeTimer: null,
    snapActiveGroup: "",
    snapAnimation: null,
    snapRunId: 0,
    startedAt: performance.now()
  };

  const currentOffset = () => {
    if (getReducedMotion()) return state.baseOffset;
    if (state.snapAnimation && typeof state.snapOffset === "number") return normalizeOffset(state.snapOffset, state.loopShift);
    if (state.paused) return state.pauseOffset;
    if (!(state.loopShift > 0) || !(state.durationMs > 0)) return 0;
    const elapsedMs = Math.max(0, performance.now() - state.startedAt);
    const speed = state.loopShift / state.durationMs;
    return normalizeOffset(state.baseOffset + (elapsedMs * speed), state.loopShift);
  };

  const setPaused = (reason, shouldPause) => {
    if (shouldPause) pauseReasons.add(reason);
    else pauseReasons.delete(reason);
    const nextPaused = pauseReasons.size > 0;
    if (nextPaused === state.paused) return;
    if (nextPaused) {
      state.pauseOffset = currentOffset();
      state.baseOffset = state.pauseOffset;
      state.startedAt = performance.now();
      state.paused = true;
      scope.classList.add("is-vis-tools-paused");
      return;
    }
    state.baseOffset = state.pauseOffset;
    state.startedAt = performance.now();
    state.paused = false;
    scope.classList.remove("is-vis-tools-paused");
  };

  const syncActive = () => {
    if (!buttons.length) return;
    if (state.snapActiveGroup) {
      setActiveGroup(buttons, state.snapActiveGroup);
      return;
    }
    if (!(state.groupWidth > 0)) return;
    const offset = normalizeOffset(currentOffset() + state.activeAnchor, state.loopShift);
    const groupIndex = Math.max(0, Math.min(groups.length - 1, Math.floor(offset / state.groupWidth) % groups.length));
    setActiveGroup(buttons, groups[groupIndex] || groups[0]);
  };

  const measure = () => {
    const oldShift = state.loopShift;
    const oldOffset = state.measured ? currentOffset() : state.baseOffset;
    const ratio = oldShift > 0 ? oldOffset / oldShift : 0;
    const first = originalCards[0];
    const second = originalCards[1];
    const firstRect = first.getBoundingClientRect();
    const computed = getComputedStyle(track);
    const gap = Number.parseFloat(computed.columnGap || computed.gap || "0") || 0;
    const distance = second ? second.offsetLeft - first.offsetLeft : 0;
    const cardStep = distance > 0 ? distance : firstRect.width + gap;
    state.durationMs = getDurationMs();
    state.groupWidth = Math.max(1, cardStep * cardsPerGroup);
    state.loopShift = Math.max(1, state.groupWidth * groupCount);
    const sliderRect = slider.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || slider.clientWidth;
    state.activeAnchor = Math.max(0, Math.min(slider.clientWidth, (viewportWidth / 2) - sliderRect.left));
    state.baseOffset = normalizeOffset(ratio * state.loopShift, state.loopShift);
    state.pauseOffset = state.paused ? state.baseOffset : state.pauseOffset;
    state.startedAt = performance.now();
    state.measured = true;
    applyLoopVars(slider, state.baseOffset, state.loopShift, state.durationMs);
    scope.classList.add("is-vis-tools-ready");
    syncActive();
  };

  const scheduleMeasure = () => {
    if (state.resizeTimer != null) window.clearTimeout(state.resizeTimer);
    state.resizeTimer = window.setTimeout(() => {
      state.resizeTimer = null;
      measure();
    }, 80);
  };

  const finishSnap = (targetOffset) => {
    const normalizedTarget = normalizeOffset(targetOffset, state.loopShift);
    const snapAnimation = state.snapAnimation;
    state.snapAnimation = null;
    state.snapOffset = normalizedTarget;
    state.baseOffset = normalizedTarget;
    state.pauseOffset = normalizedTarget;
    state.snapActiveGroup = "";
    state.startedAt = performance.now();
    if (snapAnimation) {
      try { snapAnimation.cancel(); } catch {}
    }
    track.style.transform = "";
    applyLoopVars(slider, normalizedTarget, state.loopShift, state.durationMs);
    void track.offsetWidth;
    scope.classList.remove("is-vis-tools-snapping");
    syncActive();
  };

  const snapToGroupIndex = (targetIndex) => {
    if (!(state.loopShift > 0) || !(state.groupWidth > 0)) return;
    const targetOffset = normalizeOffset((targetIndex * state.groupWidth) + (state.groupWidth / 2) - state.activeAnchor, state.loopShift);
    const targetGroup = groups[targetIndex] || groups[0];
    const snapRunId = state.snapRunId + 1;
    state.snapRunId = snapRunId;
    state.snapActiveGroup = targetGroup;
    setActiveGroup(buttons, targetGroup);
    if (getReducedMotion()) {
      scope.classList.add("is-vis-tools-snapping");
      finishSnap(targetOffset);
      return;
    }
    const startOffset = currentOffset();
    const visualTarget = chooseClosestTarget(startOffset, targetOffset, state.loopShift);
    if (state.snapAnimation) {
      try { state.snapAnimation.cancel(); } catch {}
      state.snapAnimation = null;
    }
    setPaused("snap", false);
    scope.classList.add("is-vis-tools-snapping");
    track.style.transform = `translateX(${(-startOffset).toFixed(3)}px)`;
    if (typeof track.animate !== "function") {
      track.style.transition = `transform ${SNAP_DURATION_MS}ms cubic-bezier(0.22, 0.61, 0.36, 1)`;
      track.style.transform = `translateX(${(-visualTarget).toFixed(3)}px)`;
      window.setTimeout(() => {
        if (state.snapRunId !== snapRunId) return;
        track.style.transition = "";
        finishSnap(targetOffset);
      }, SNAP_DURATION_MS);
      return;
    }
    const animation = track.animate(
      [
        { transform: `translateX(${(-startOffset).toFixed(3)}px)` },
        { transform: `translateX(${(-visualTarget).toFixed(3)}px)` }
      ],
      {
        duration: SNAP_DURATION_MS,
        easing: "cubic-bezier(0.22, 0.61, 0.36, 1)",
        fill: "forwards"
      }
    );
    state.snapAnimation = animation;
    animation.finished
      .then(() => {
        if (state.snapAnimation === animation && state.snapRunId === snapRunId) finishSnap(targetOffset);
      })
      .catch(() => {});
  };

  const onVisibility = () => {
    setPaused("visibility", document.hidden);
  };
  const canHoverPause = () => window.matchMedia?.("(hover: hover) and (pointer: fine)")?.matches === true;
  const onPointerEnter = () => {
    if (canHoverPause()) setPaused("hover", true);
  };
  const onPointerLeave = () => {
    setPaused("hover", false);
  };
  const onFocusIn = () => {
    if (canHoverPause()) setPaused("focus", true);
  };
  const onFocusOut = () => {
    setPaused("focus", false);
  };
  const onReducedMotionChange = () => {
    measure();
  };

  const buttonHandlers = buttons.map((button) => {
    const onClick = (event) => {
      event.preventDefault();
      const group = button.dataset.visGroupTarget || "";
      const targetIndex = Math.max(0, groups.indexOf(group));
      snapToGroupIndex(targetIndex);
    };
    button.addEventListener("click", onClick);
    return { button, onClick };
  });

  slider.addEventListener("pointerenter", onPointerEnter);
  slider.addEventListener("pointerleave", onPointerLeave);
  slider.addEventListener("focusin", onFocusIn);
  slider.addEventListener("focusout", onFocusOut);
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("resize", scheduleMeasure, { passive: true });
  const motionMedia = window.matchMedia?.("(prefers-reduced-motion: reduce)");
  motionMedia?.addEventListener?.("change", onReducedMotionChange);
  if (document.fonts?.ready) document.fonts.ready.then(scheduleMeasure).catch(() => {});

  measure();
  const activeTimer = window.setInterval(syncActive, ACTIVE_INTERVAL_MS);

  const cleanup = () => {
    if (state.resizeTimer != null) window.clearTimeout(state.resizeTimer);
    window.clearInterval(activeTimer);
    state.snapRunId += 1;
    state.snapActiveGroup = "";
    if (state.snapAnimation) {
      try { state.snapAnimation.cancel(); } catch {}
    }
    clones.forEach((clone) => clone.remove());
    buttonHandlers.forEach(({ button, onClick }) => button.removeEventListener("click", onClick));
    slider.removeEventListener("pointerenter", onPointerEnter);
    slider.removeEventListener("pointerleave", onPointerLeave);
    slider.removeEventListener("focusin", onFocusIn);
    slider.removeEventListener("focusout", onFocusOut);
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("resize", scheduleMeasure);
    motionMedia?.removeEventListener?.("change", onReducedMotionChange);
    scope.classList.remove("is-vis-tools-ready", "is-vis-tools-paused", "is-vis-tools-snapping");
    track.style.transform = "";
    track.style.transition = "";
  };

  const instance = { cleanup };
  sliderInstances.set(scope, instance);
  return instance;
}

export function initVisToolsSlider(root = document) {
  const scopes = root instanceof Element && root.matches(VIS_TOOLS_SLIDER_SELECTOR)
    ? [root]
    : [...root.querySelectorAll(VIS_TOOLS_SLIDER_SELECTOR)];
  scopes.forEach((scope) => initOneVisToolsSlider(scope));
}
